/**
 * Juice palettes: what every cartoon effect is coloured with.
 *
 * - Puff tones ({ base, light, shade, ink }) are baked into the FX atlas, one picture per tone
 *   and puff shape, so a palette name here must exist before the atlas is baked.
 * - Blast palettes (after Ghost Circuit's BLAST_PALETTES) name which puff tones a blast cools
 *   through as it ages, its smoke, the starburst's rim and heart, the ring and the sparks.
 * - Tier palettes follow each creature's colour family from the old art sheet, so a merge
 *   explodes in the colour of the creature it makes.
 */
import { INK, PUFF_TONES, mixHex } from "../toon/palette.js";

/** One dominant colour per evolution tier (index = tier id), with its spark accent. */
export var TIER_COLORS = [
  { base: "#6fd36a", accent: "#e8ffb8" }, // 0 cell: jelly green
  { base: "#ff6a5c", accent: "#ffe0a0" }, // 1 starfish: coral red
  { base: "#ff8fd0", accent: "#ffe4f6" }, // 2 jellyfish: bubblegum pink
  { base: "#ffa13a", accent: "#fff1a8" }, // 3 fish: sunny orange
  { base: "#ffd447", accent: "#fff8d0" }, // 4 snail: golden yellow
  { base: "#3a6fd8", accent: "#c4e2ff" }, // 5 bug: metallic blue
  { base: "#a99cff", accent: "#ffd6f4" }, // 6 butterfly: lilac
  { base: "#3fcf8e", accent: "#d8ffe8" }, // 7 lizard: emerald
  { base: "#c77dff", accent: "#f2dcff" }, // 8 pterodactyl: purple
  { base: "#ff7f3a", accent: "#ffe08a" }, // 9 t-rex: lava orange
  { base: "#2ed3c6", accent: "#ffe27a" }, // 10 dragon: teal with gold
];

/** Tones of a tier colour: light toward cream, shade sinking toward ink (never to black). */
function tierTone(base) {
  return {
    base: base,
    light: mixHex(base, "#ffffff", 0.55),
    shade: mixHex(base, INK, 0.28),
    ink: INK,
  };
}

/** The darker, cooling stage of a tier blast. */
function tierDeepTone(base) {
  var deep = mixHex(base, INK, 0.3);
  return {
    base: deep,
    light: mixHex(base, "#ffffff", 0.15),
    shade: mixHex(base, INK, 0.5),
    ink: INK,
  };
}

/** Every puff tone the atlas bakes (shared PUFF_TONES plus the juice extras and tiers). */
export var JUICE_PUFF_TONES = (function () {
  var out = {};
  for (var k in PUFF_TONES) {
    if (Object.prototype.hasOwnProperty.call(PUFF_TONES, k)) out[k] = PUFF_TONES[k];
  }
  // A hot white-cream: the first instant of a creature blast, before it takes the tier colour.
  out.cream = { base: "#fff3d0", light: "#ffffff", shade: "#f2cf8e", ink: INK };
  // Glass dust: pale cyan, inked in a deep cyan of its own so it reads as glitter, not rock.
  out.glass = { base: "#d8f4ff", light: "#ffffff", shade: "#94d2ee", ink: "#2c6a8c" };
  // Rock dust: grey-brown vapour with its own soft ink.
  out.stone = { base: "#c4bab0", light: "#ece5dc", shade: "#958a80", ink: "#574e47" };
  // Water splash in the jar (night): soft aqua.
  out.water = { base: "#bfeaff", light: "#ffffff", shade: "#7cc8ec", ink: "#2f6f96" };
  out.heart = { base: "#ff6f8a", light: "#ffc2cf", shade: "#d8405e", ink: INK };
  for (var i = 0; i < TIER_COLORS.length; i++) {
    out["tier" + i] = tierTone(TIER_COLORS[i].base);
    out["tierDeep" + i] = tierDeepTone(TIER_COLORS[i].base);
  }
  return out;
})();

/**
 * Blast palettes: `fire` is the puff tones a blast cools through (young → old), `smoke` its
 * smoke tone, `flash` the starburst's heart, `rim` its body, `ring` / `ringRim` the shock ring,
 * `spark` two alternating spark colours, `glow` the soft emitter light (optional).
 */
export var BLAST_PALETTES = {
  fire: { fire: ["hot", "fire", "ember"], smoke: "smoke", flash: "#fff6c4", rim: "#ffb03a", ring: "#ffd27a", ringRim: "#ffb03a", spark: ["#fff1a8", "#ffb03a"], glow: "#ffd27a" },
  gold: { fire: ["hot", "gold", "fire"], smoke: "smoke", flash: "#fffbe6", rim: "#ffc84a", ring: "#fff1c4", ringRim: "#ffc84a", spark: ["#fffbe6", "#ffd45c"], glow: "#ffe08a" },
  // A comic "POW" for hits: pale yellow heart, warm rim.
  impact: { fire: ["cream", "hot", "smoke"], smoke: "smoke", flash: "#ffffff", rim: "#ffe27a", ring: "#fff6c4", ringRim: "#ffb03a", spark: ["#ffffff", "#ffe27a"], glow: "#fff6c4" },
  glass: { fire: ["glass", "glass", "smoke"], smoke: "smoke", flash: "#ffffff", rim: "#a8e4ff", ring: "#e8f9ff", ringRim: "#7cc8ec", spark: ["#ffffff", "#bdeeff"], glow: "#d8f4ff" },
  dust: { fire: ["dust", "dust", "dust"], smoke: "dust", flash: "#fff6dc", rim: "#e6c58e", ring: "#f6ead0", ringRim: "#c8b28c", spark: ["#f6ead0", "#c8b28c"] },
  stone: { fire: ["stone", "stone", "smoke"], smoke: "stone", flash: "#fff6dc", rim: "#d8cfc4", ring: "#f2ece4", ringRim: "#a59a8e", spark: ["#efe7dc", "#a59a8e"] },
  gem: { fire: ["pink", "pink", "violet"], smoke: "smoke", flash: "#ffffff", rim: "#ff8fd0", ring: "#ffe4f6", ringRim: "#ff6fd8", spark: ["#ffffff", "#ff9cf5"], glow: "#ffc8ee" },
  heart: { fire: ["heart", "pink", "heart"], smoke: "smoke", flash: "#fff0f4", rim: "#ff6f8a", ring: "#ffd0da", ringRim: "#ff6f8a", spark: ["#ffffff", "#ff9bb0"], glow: "#ffc2cf" },
  water: { fire: ["water", "foam", "water"], smoke: "foam", flash: "#ffffff", rim: "#9fdcff", ring: "#e8f9ff", ringRim: "#7cc8ec", spark: ["#ffffff", "#bfeaff"] },
  violet: { fire: ["violet", "violet", "smoke"], smoke: "smoke", flash: "#f6f0ff", rim: "#b49cff", ring: "#ddd0ff", ringRim: "#9a7cff", spark: ["#ffffff", "#cbb8ff"], glow: "#ddd0ff" },
  spirit: { fire: ["spirit", "spirit", "smoke"], smoke: "smoke", flash: "#ffffff", rim: "#7cc8ff", ring: "#bfe6ff", ringRim: "#7cc8ff", spark: ["#ffffff", "#9fdcff"], glow: "#bfe6ff" },
};

(function addTierPalettes() {
  for (var i = 0; i < TIER_COLORS.length; i++) {
    var t = TIER_COLORS[i];
    var tone = JUICE_PUFF_TONES["tier" + i];
    BLAST_PALETTES["tier" + i] = {
      fire: ["cream", "tier" + i, "tierDeep" + i],
      smoke: "smoke",
      flash: "#fffbe6",
      rim: t.base,
      ring: tone.light,
      ringRim: t.base,
      spark: [t.accent, tone.light],
      glow: tone.light,
    };
  }
})();

/** The blast palette for an evolution tier (clamped to the known tiers). */
export function tierPaletteKey(tier) {
  var i = Math.max(0, Math.min(TIER_COLORS.length - 1, Math.floor(Number(tier) || 0)));
  return "tier" + i;
}

/** A tier's dominant colour (hex). */
export function tierColor(tier) {
  var i = Math.max(0, Math.min(TIER_COLORS.length - 1, Math.floor(Number(tier) || 0)));
  return TIER_COLORS[i].base;
}

/**
 * What things break into: a light and a dark tone per material, each baked as inked chunk
 * polygons with a lit facet (Ghost Circuit's CHUNKS, plus glass for the flask).
 */
export var CHUNK_TONES = {
  rock: ["#b3a89c", "#857a6e"],
  glass: ["#e2f7ff", "#9ad8f2"],
  gem: ["#ff9cf5", "#c43cc0"],
  gold: ["#ffd45c", "#e5951f"],
};

/** Resolve a palette name or object to a blast palette. */
export function resolveBlastPalette(p) {
  if (p && typeof p === "object") return p;
  return BLAST_PALETTES[p] || BLAST_PALETTES.fire;
}
